import { AfterViewInit, computed, Directive, ElementRef, inject, signal } from '@angular/core';
import { TableColumnDefUIDirective } from './table-column-def.directive';
import { TableResizeUIService } from '../services/table-resize.service';
import { RESIZE_HANDLE_ID } from '../constants/resize-handle.constants';

@Directive({
  selector: '[fr8botUiTableColumnResize]',
  host: {
    '[class.table-column-resizing]': 'isResizing()',
    '[class.table-column-resizable]': 'isResizable()',
    '[class.table-fill-column]': 'isFillColumn()',
    '(mousedown)': 'onResizeStart($event)',
    '(document:mousemove)': 'onResizeMove($event)',
    '(document:mouseup)': 'onResizeEnd()',
  },
})
export class TableColumnResizeUIDirective implements AfterViewInit {
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef, { host: true });
  private column = inject<TableColumnDefUIDirective<unknown>>(TableColumnDefUIDirective);
  private resizeService = inject(TableResizeUIService);

  protected readonly isResizing = signal(false);
  /** A menu column has a fixed width. */
  readonly isResizable = computed(() => !this.column.isMenu());
  /** The last non-menu column, which stretches over the table's free space. */
  readonly isFillColumn = computed(() => {
    const columnName = this.column.name();

    return columnName !== null && columnName === this.resizeService.fillColumn();
  });

  onResizeStart(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (target.id === RESIZE_HANDLE_ID && this.isResizable()) {
      event.preventDefault();
      event.stopPropagation();

      this.isResizing.set(true);
      this.resizeService.isTableResizing.set(true);
      this.startX = event.clientX;
      this.initialWidth = this.elementRef.nativeElement.clientWidth;
      this.stretchedFromWidth = this.stretchedFrom();
    }
  }

  onResizeMove(event: MouseEvent): void {
    if (!this.isResizing()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const columnName = this.column.name();
    const width = this.initialWidth + event.clientX - this.startX;

    if (columnName) {
      this.resizeService.updateColumnWidth(
        columnName,
        this.stretchedFromWidth !== null && width <= this.initialWidth
          ? this.stretchedFromWidth
          : width,
      );
    }
  }

  onResizeEnd(): void {
    if (!this.isResizing()) {
      return;
    }

    this.isResizing.set(false);
    this.stretchedFromWidth = null;
    this.resizeService.isTableResizing.set(false);
    this.resizeService.emitResizeEnd();
  }

  private initialWidth = 0;
  private stretchedFromWidth: number | null = null;
  private startX = 0;

  constructor() {
    const column = this.column;
    const resizeService = this.resizeService;

    resizeService.markColumnResizable(column.name());
  }

  /**
   * While the fill column is stretched over free space, dragging it narrower would only lower a
   * width nobody sees, so the drag keeps the width it was set to — storing a width between the
   * two would pin one that depends on the window and scroll the table on a narrower one. Dragging
   * past the stretched width widens the table on purpose and stores the new width.
   */
  private stretchedFrom(): number | null {
    const columnName = this.column.name();

    if (!columnName || columnName !== this.resizeService.fillColumn()) {
      return null;
    }

    const width = this.resizeService.columnWidth(columnName);

    // `clientWidth` is rounded; a fractional saved width must not read as stretched.
    return width !== null && this.initialWidth > width + 1 ? width : null;
  }

  ngAfterViewInit(): void {
    const columnName = this.column.name();
    if (columnName) {
      this.resizeService.markColumnAsReady(columnName);
    }
  }
}
